import { forceUpdate } from "@stencil/core";
import { getLocale, SupportedLocale } from "./i18n";

type LocaleStore = {
  state: { locale?: string };
  onChange: (prop: "locale", callback: (locale?: string) => void) => () => void;
};

type ComponentWithDisconnect = {
  disconnectedCallback?: () => void;
};

type WatchStoreLocaleOptions = {
  // Skip forceUpdate when locale callback already updates @State and triggers rerender.
  skipForceUpdate?: boolean;
};

const cleanupHandlers = new WeakMap<ComponentWithDisconnect, Set<() => void>>();
const wrappedDisconnect = new WeakSet<ComponentWithDisconnect>();

const registerCleanup = (
  target: ComponentWithDisconnect,
  cleanup: () => void
) => {
  let handlers = cleanupHandlers.get(target);

  if (handlers === undefined) {
    handlers = new Set<() => void>();
    cleanupHandlers.set(target, handlers);
  }

  handlers.add(cleanup);

  if (wrappedDisconnect.has(target)) {
    return;
  }

  const originalDisconnected = target.disconnectedCallback?.bind(target);

  target.disconnectedCallback = () => {
    const currentHandlers = cleanupHandlers.get(target);

    if (currentHandlers !== undefined) {
      currentHandlers.forEach((handler) => {
        handler();
      });
      currentHandlers.clear();
    }

    if (originalDisconnected !== undefined) {
      originalDisconnected();
    }
  };

  wrappedDisconnect.add(target);
};

export const watchStoreLocale = (
  target: ComponentWithDisconnect,
  store: LocaleStore,
  onLocaleChange?: (locale: SupportedLocale) => void,
  options?: WatchStoreLocaleOptions
): (() => void) => {
  let activeLocale: SupportedLocale | undefined;

  // Trigger optional callback and force rerender on locale changes.
  const applyLocale = (inputLocale?: string) => {
    const locale = getLocale(inputLocale);
    const previousLocale = activeLocale;
    activeLocale = locale;

    if (onLocaleChange !== undefined) {
      onLocaleChange(locale);
    }

    if (
      !options?.skipForceUpdate &&
      previousLocale !== undefined &&
      previousLocale !== locale
    ) {
      forceUpdate(target as object);
    }
  };

  applyLocale(store.state.locale);

  const unsubscribe = store.onChange("locale", applyLocale);
  let disposed = false;
  const cleanup = () => {
    if (disposed) return;
    disposed = true;
    unsubscribe();
  };

  registerCleanup(target, cleanup);

  return cleanup;
};
